import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { ArrowLeft, Calendar, User, CheckCircle2, Layers, ExternalLink, X } from 'lucide-react';

import SEO from '../components/SEO';
import { useTranslation } from 'react-i18next';

const ProjectDetail: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const { projects } = useData();
    const { i18n } = useTranslation();
    const [selectedImage, setSelectedImage] = React.useState<string | null>(null);

    const project = projects.find(p => p.slug === slug);

    useEffect(() => {
        if (!project) {
            navigate('/projelerimiz');
        }
        window.scrollTo(0, 0);
    }, [project, navigate]);

    if (!project) return null;

    return (
        <div className="w-full bg-brand-light min-h-screen">
            <SEO
                title={project.title}
                description={project.description}
                link={`/projelerimiz/${project.slug}`}
                image={project.image}
                type="article"
            />
            
            {/* Lightbox Modal */}
            {selectedImage && (
                <div 
                    className="fixed inset-0 z-[100] bg-brand-dark/95 backdrop-blur-md flex items-center justify-center p-4 md:p-12 cursor-zoom-out"
                    onClick={() => setSelectedImage(null)}
                >
                    <button 
                        className="absolute top-8 right-8 text-white/50 hover:text-white transition-colors p-2"
                        onClick={() => setSelectedImage(null)}
                    >
                        <X size={40} />
                    </button>
                    <img 
                        src={selectedImage} 
                        alt="Zoomed" 
                        className="max-w-full max-h-full object-contain rounded-lg shadow-2xl animate-in zoom-in duration-300" 
                    />
                </div>
            )}

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 bg-brand-dark text-white overflow-hidden">
                <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-brand-blue/10 to-transparent"></div>
                <div className="container mx-auto px-4 md:px-8 relative z-10">
                    <Link to="/projelerimiz" className="inline-flex items-center text-gray-400 hover:text-white mb-8 transition-colors">
                        <ArrowLeft size={20} className="mr-2" /> Tüm Projelere Dön
                    </Link>

                    <div className="flex flex-col md:flex-row gap-4 items-start md:items-center mb-6">
                        <span className="bg-brand-blue/20 text-brand-blue px-4 py-1.5 rounded-full text-[10px] font-bold tracking-widest border border-brand-blue/20">
                            {project.category.toLocaleUpperCase(i18n.language === 'tr' ? 'tr-TR' : 'en-US')}
                        </span>
                        {project.year && (
                            <span className="flex items-center text-gray-400 text-sm font-bold">
                                <Calendar size={16} className="mr-2" /> {project.year}
                            </span>
                        )}
                        {project.client && (
                            <span className="flex items-center text-gray-400 text-sm font-bold">
                                <User size={16} className="mr-2" /> {project.client}
                            </span>
                        )}
                    </div>

                    <h1 className="text-4xl md:text-7xl font-bold mb-8 leading-tight tracking-tight">
                        {project.title}
                    </h1>
                    <p className="text-xl text-gray-300 max-w-3xl leading-relaxed font-medium">
                        {project.description}
                    </p>

                    {project.link && (
                        <a
                            href={project.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center mt-10 bg-brand-blue hover:bg-blue-600 text-white px-10 py-4 rounded-2xl font-bold transition-all transform hover:scale-105 shadow-xl shadow-brand-blue/20"
                        >
                            Projeyi Ziyaret Et <ExternalLink size={20} className="ml-3" />
                        </a>
                    )}
                </div>
            </section>

            {/* Main Content */}
            <section className="py-24">
                <div className="container mx-auto px-4 md:px-8">
                    <div className="grid lg:grid-cols-3 gap-16">
                        {/* Details Sidebar */}
                        <div className="lg:col-span-1 space-y-10">
                            {project.technologies && project.technologies.length > 0 && project.technologies[0] !== '' && (
                                <div className="bg-white p-10 rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100">
                                    <h3 className="text-xl font-bold text-brand-dark mb-8 flex items-center tracking-tight">
                                        <Layers size={24} className="mr-3 text-brand-blue" /> Kullanılan Teknolojiler
                                    </h3>
                                    <div className="flex flex-wrap gap-3">
                                        {project.technologies.map((tech, idx) => (
                                            <span key={idx} className="bg-brand-light text-brand-dark border border-gray-100 px-4 py-2 rounded-xl text-xs font-bold transition-colors hover:border-brand-blue/20 hover:bg-white">
                                                {tech}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="bg-brand-dark text-white p-10 rounded-[2.5rem] shadow-2xl shadow-brand-dark/20 relative overflow-hidden group">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-brand-blue/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-700"></div>
                                <h3 className="text-2xl font-bold mb-4 relative z-10 tracking-tight">Projeniz mi var?</h3>
                                <p className="text-gray-400 mb-8 relative z-10 font-medium leading-relaxed">
                                    Sizin için de benzer bir başarı hikayesi yazalım. Hemen iletişime geçin.
                                </p>
                                <button
                                    onClick={() => navigate('/iletisim')}
                                    className="w-full bg-brand-blue hover:bg-blue-600 text-white py-4 rounded-2xl font-bold transition-all relative z-10 shadow-lg"
                                >
                                    Teklif Alın
                                </button>
                            </div>
                        </div>

                        {/* Content Body */}
                        <div className="lg:col-span-2 space-y-16">
                            {project.challenge && (
                                <div className="animate-in slide-in-from-bottom-8 duration-500">
                                    <h3 className="text-2xl font-bold text-brand-dark mb-6 tracking-tight flex items-center">
                                        <span className="w-8 h-1 bg-brand-blue rounded-full mr-4"></span>
                                        Zorluklar ve Hedefler
                                    </h3>
                                    <p className="text-gray-600 text-lg leading-relaxed font-normal whitespace-pre-line">
                                        {project.challenge}
                                    </p>
                                </div>
                            )}

                            {project.solution && (
                                <div className="animate-in slide-in-from-bottom-8 duration-500 delay-100">
                                    <h3 className="text-2xl font-bold text-brand-dark mb-6 tracking-tight flex items-center">
                                        <span className="w-8 h-1 bg-brand-blue rounded-full mr-4"></span>
                                        Çözüm Yaklaşımımız
                                    </h3>
                                    <p className="text-gray-600 text-lg leading-relaxed font-normal whitespace-pre-line">
                                        {project.solution}
                                    </p>
                                </div>
                            )}

                            {project.result && (
                                <div className="bg-blue-50/50 p-10 rounded-[2.5rem] border border-brand-blue/10 animate-in slide-in-from-bottom-8 duration-500 delay-200">
                                    <h3 className="text-2xl font-bold text-brand-blue mb-6 flex items-center tracking-tight">
                                        <CheckCircle2 size={32} className="mr-4" /> Sonuçlar
                                    </h3>
                                    <p className="text-brand-dark/80 text-lg leading-relaxed font-medium whitespace-pre-line">
                                        {project.result}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Gallery */}
                    {project.images && project.images.length > 0 && project.images[0] !== '' && (
                        <div className="mt-32">
                            <h3 className="text-3xl font-bold text-brand-dark mb-12 text-center tracking-tight">Proje Detayları</h3>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {project.images.map((img, idx) => (
                                    <div 
                                        key={idx} 
                                        className="group rounded-3xl overflow-hidden shadow-xl shadow-gray-200/50 border border-gray-100 h-80 cursor-zoom-in relative"
                                        onClick={() => setSelectedImage(img)}
                                    >
                                        <img 
                                            src={img} 
                                            alt={`${project.title} - ${idx + 1}`} 
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
                                        />
                                        <div className="absolute inset-0 bg-brand-dark/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg transform scale-50 group-hover:scale-100 transition-transform duration-300">
                                                <ExternalLink className="text-brand-blue" size={20} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                </div>
            </section>
        </div>
    );
};

export default ProjectDetail;
